/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import { cssMap, jsx } from '@atlaskit/css';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type ConfluenceFolderCreatePayload } from '@atlassian/conversation-assistant-message-actions-api/confluence';
import { type ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	type ActionRendererFor,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { useConfluenceEntities } from '@atlassian/conversation-assistant-service/services/confluence/useConfluenceEntities';
import { OptionIcon } from '@atlassian/rovo-platform-ui-components/option-icon';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';

import messages from './messages';

export const ConfluenceFolderCreateAction = ({
	actions,
	cloudId,
	conversationId,
	getStatus,
	messageId,
	recipientAgentId,
	sendMessageConfirmedTools,
}: ActionRendererFor<typeof ActionKeys.ConfluenceFolderCreate>): JSX.Element => {
	const { formatMessage } = useIntl();
	const { getEntity } = useConfluenceEntities(cloudId);
	const isMulti = actions.length > 1;

	// Planned and finished folders start checked (matching ResumptionRenderer) — finished ones render
	// checked-but-locked. In single mode the sole folder stays selected (no checkbox is shown), so the
	// confirm logic below is identical for both.
	const [selectedIds, setSelectedIds] = useState<Set<string>>(
		() =>
			new Set(
				actions
					.filter((action) => isPlanned(action) || action.status === 'finished')
					.map((action) => action.invocationId),
			),
	);

	const toggle = useCallback((invocationId: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(invocationId)) {
				next.delete(invocationId);
			} else {
				next.add(invocationId);
			}
			return next;
		});
	}, []);

	// A folder's checkbox is locked once it's no longer planned, or while/after it's being submitted.
	const isLocked = useCallback(
		(action: MessageAction) => !isPlanned(action) || getStatus(action) !== 'idle',
		[getStatus],
	);

	const hasPlanned = actions.some(isPlanned);
	const isSubmitting = actions.some((action) => getStatus(action) === 'started');
	const hasActionableSelection = actions.some(
		(action) => isPlanned(action) && selectedIds.has(action.invocationId),
	);
	const isCreateDisabled = !hasActionableSelection || isSubmitting;

	// Confirm/cancel via workflow resumption, forwarding the ORIGINAL data (raw `container` preserved).
	// Only still-planned folders are sent; selection is encoded per-tool (selected -> confirmed).
	const resolve = useCallback(
		(confirm: boolean) => {
			const plannedActions = actions.filter(isPlanned);
			const isConfirmed = (action: MessageAction) =>
				confirm && selectedIds.has(action.invocationId);
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: { interactionSource: 'confluenceFolderCreateRenderer' },
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: plannedActions.map((action) =>
						buildToolFromAction(action, isConfirmed(action) ? 'confirmed' : 'cancelled'),
					),
				},
				resolutionType: plannedActions.some(isConfirmed) ? 'confirmed' : 'cancelled',
			});
		},
		[actions, conversationId, messageId, recipientAgentId, selectedIds, sendMessageConfirmedTools],
	);

	return (
		<ActionCardWrapper
			title={formatMessage(messages.cardTitle, { count: actions.length })}
			product="confluence"
		>
			<Stack space="space.150">
				{actions.map((action) => {
					const folderElement = <FolderComponent action={action} getEntity={getEntity} />;
					// Single folder renders inline; multiple folders each get their own bordered card
					// (a title-less nested ActionCardWrapper) for clearer visual separation, matching the
					// generic ResumptionRenderer.
					return isMulti ? (
						<ActionCardWrapper key={action.invocationId}>
							<Checkbox
								isChecked={selectedIds.has(action.invocationId)}
								onChange={() => toggle(action.invocationId)}
								isDisabled={isLocked(action)}
								label={folderElement}
							/>
						</ActionCardWrapper>
					) : (
						<Box key={action.invocationId}>{folderElement}</Box>
					);
				})}
			</Stack>
			<ActionCardFooter>
				<Box xcss={styles.footer}>
					<Button
						appearance="primary"
						isLoading={isSubmitting}
						isDisabled={isCreateDisabled}
						onClick={() => resolve(true)}
					>
						{formatMessage(isMulti ? messages.createSelectedButton : messages.createButton)}
					</Button>
					<Button isDisabled={isSubmitting || !hasPlanned} onClick={() => resolve(false)}>
						{formatMessage(messages.cancelButton)}
					</Button>
				</Box>
			</ActionCardFooter>
		</ActionCardWrapper>
	);
};

// Shared per-folder display — identical whether rendered standalone (single) or as a checkbox label (multi).
const FolderComponent = ({
	action,
	getEntity,
}: {
	action: ActionWith<ConfluenceFolderCreatePayload>;
	getEntity: ReturnType<typeof useConfluenceEntities>['getEntity'];
}): JSX.Element => {
	const { formatMessage } = useIntl();
	const { data } = action;
	const [space, setSpace] = useState<{ name: string; iconPath?: string } | undefined>(undefined);

	// Resolve the raw container id to a human-readable space icon and name for display.
	useEffect(() => {
		let ignore = false;
		const fetchSpace = async () => {
			if (data.container) {
				const resolved = await getEntity(data.container, 'space');
				if (resolved && !ignore) {
					setSpace({ name: resolved.name, iconPath: resolved.icon?.path });
				}
			}
		};
		fetchSpace();
		return () => {
			ignore = true;
		};
	}, [data.container, getEntity]);

	const spaceElement = space ? (
		<Inline space="space.050" alignBlock="center">
			<OptionIcon type="space" iconPath={space.iconPath} />
			<Text>{space.name}</Text>
		</Inline>
	) : (
		<Text>{data.container}</Text>
	);

	return (
		<Stack space="space.100">
			<Inline space="space.100" alignBlock="center">
				<Box xcss={styles.label}>
					<Text weight="bold">{formatMessage(messages.spaceLabel)}:</Text>
				</Box>
				{spaceElement}
			</Inline>
			<Inline space="space.100" alignBlock="center">
				<Box xcss={styles.label}>
					<Text weight="bold">{formatMessage(messages.folderNameLabel)}:</Text>
				</Box>
				<Text>{data.name}</Text>
			</Inline>
		</Stack>
	);
};

// A folder is actionable only while its backend status is `planned`; any other status is locked.
const isPlanned = (action: MessageAction): boolean => action.status === 'planned';

const styles = cssMap({
	footer: {
		display: 'flex',
		gap: token('space.100'),
		justifyContent: 'flex-end',
	},
	label: {
		whiteSpace: 'nowrap', // Keep the bold label (incl. its colon) on one line; the value wraps instead when space is tight.
	},
});
